import {useEffect} from 'react';
import {Linking} from 'react-native';
import {deepLinkingTypes} from '../enums';
import {store} from '../../redux/store';
import screens from '../../config/screens';
import Config from 'react-native-config';
import {getShareLinkNewsDetails} from '../../screens/content/latestArticle/actions';
import {setIsOnboardingSeen} from '../../screens/auth/onboarding/reducer';
import {SharedLinkDetailsData} from '../../interfaces/api/tabs';
import {navigationRef} from '../../navigation';

/**
 * Custom hook to handle deep linking.
 * This hook handles the URL processing internally.
 */
const useDeepLinking = (): void => {
  /**
   * Opens a deep link in a mobile app.
   * If the deep link fails, it can optionally redirect to a fallback URL.
   *
   * @param {string} deepLink - The deep link URL to open the mobile app.
   */
  const openDeepLink = (deepLink: string) => {
    try {
      const anchor = document.createElement('a');
      anchor.href = deepLink;
      anchor.rel = 'noopener noreferrer';
      document.body.appendChild(anchor);

      // Attempt to open the deep link
      anchor.click();

      // Remove the anchor after use
      document.body.removeChild(anchor);
    } catch (error) {
    }
  };

  useEffect(() => {
    // Add event listener for deep links
    const subscription = Linking.addEventListener('url', handleDeepLink);
    Linking.getInitialURL().then(url => {
      if (url) {
        handleDeepLink({url});
      }
    });
    return () => {
      subscription.remove();
    };
  }, []);

  // Function to handle the deep link
  const handleDeepLink = ({url}: {url: string}) => {
    const cleaned = (url || '')
      .replace('http://localhost:4200/', '')
      .replace(Config.UNIVERSAL_DEEP_LINK_SCHEME + '/' || '', '');

    const parts = cleaned.split('/').filter(Boolean);
    const first = parts[0] || '';

    // Support both shared-article/:token and shared/chats/:id
    if (first === deepLinkingTypes.sharedArticle) {
      handleShareArticleCallback(url);
      return;
    }

    if (first === 'shared' && parts[1] === 'chats' && parts[2]) {
      handleShareChatCallback(parts[2]);
      return;
    }

    if (first === deepLinkingTypes.sharedChat && parts[1]) {
      handleShareChatCallback(parts[1]);
      return;
    }
  };

  const handleShareArticleCallback = async (url?: string) => {
    try {
      const params = (url || '').split('/');
      const token = params[params.length - 1];
      store.dispatch(setIsOnboardingSeen({isSeen: true}));
      const res = await getShareLinkNewsDetails(token);
      //open deeplink on mobile app
      if (!res) return;
      const mobileParams = new URLSearchParams();
      (Object.keys(res) as (keyof SharedLinkDetailsData)[]).forEach(key => {
        if (res?.[key]) {
          mobileParams.append(key, String(res?.[key]));
        }
      });
      openDeepLink(
        `${Config.DEEP_LINK_SCHEME}article?${mobileParams.toString()}`,
      );
      // @ts-ignore - navigate with params for shared article
      navigationRef?.current?.navigate(screens.sharedArticle, {
        item: res?.model || {},
        isRecommendation: res?.type === 'Recommendation',
        isRecommendationByTab: res?.type === 'Summary',
        isSummary: res?.type === 'Summary',
      });
    } catch (err: any) {
    }
  };

  const handleShareChatCallback = async (chatId: string) => {
    try {
      store.dispatch(setIsOnboardingSeen({isSeen: true}));
      // For mobile deep link, route to the same shared chat path
      openDeepLink(`${Config.DEEP_LINK_SCHEME}shared/chats/${chatId}`);
      // Navigate on web to the public shared chat screen
      // @ts-ignore - navigate with params for shared chat
      navigationRef?.current?.navigate(screens.sharedChat, {chatId});
    } catch (err) {
    }
  };
};

export default useDeepLinking;
